// client/src/data/pricingContent.js
// Public pricing page content - bilingual tier definitions
// Source of truth for tier limits: server/utils/pricingTiers.js

export const pricingContent = {
  en: {
    heroTitle: 'Simple pricing that grows with you',
    heroSubtitle: 'Every tier includes unlimited orders, unlimited customers, and bilingual support. Pick the plan that fits your catalog size and feature needs.',
    businessCalloutTitle: 'Businesses order for free',
    businessCalloutBody: 'WholesaleHub is always free for businesses — no subscription, no transaction fees, no hidden costs. Pricing below applies only to wholesalers.',
    popularBadge: 'Most popular',
    contactBadge: 'Custom',
    monthSuffix: '/month',
    contactPrice: 'Contact us',
    perOrderLabel: 'per-order fee',
    ctaStarter: 'Start with Starter',
    ctaProfessional: 'Choose Professional',
    ctaEnterprise: 'Contact sales',
    ctaRegister: 'Create wholesaler account',
    compareTitle: 'Feature comparison',
    compareIntro: 'All prices exclude VAT. Limits below reflect the current platform configuration.',
    faqLinkText: 'Frequently asked questions',
    helpLinkText: 'Help Center',
    contactEmail: 'sales@wholesalehub.gr',

    tiers: [
      {
        id: 'starter',
        name: 'Starter',
        price: 49,
        color: 'blue',
        productLimit: '1,500',
        perOrderFee: 0.40,
        tagline: 'Everything you need to get online and start taking orders',
        target: 'Small wholesalers',
        highlights: [
          'Up to 1,500 products',
          '€0.40 per-order fee',
          '5 customer-specific discounts',
          '5 catalog-wide discounts',
          '10 product-specific discounts',
          '1 team member',
          'Bulk CSV product upload',
          'Invoice uploads',
          'Email support (48hr response)'
        ],
        cta: 'ctaStarter',
        ctaHref: '/wholesaler/register'
      },
      {
        id: 'professional',
        name: 'Professional',
        price: 129,
        color: 'purple',
        productLimit: '5,000',
        perOrderFee: 0.25,
        popular: true,
        tagline: 'For growing wholesalers with regular customers and an active sales flow',
        target: 'Growing wholesalers',
        highlights: [
          'Up to 5,000 products',
          '€0.25 per-order fee',
          'Unlimited customer discounts',
          'Unlimited product discounts',
          '50 catalog-wide discounts',
          '5 flash offers per month',
          'Private catalog mode',
          '1 sales rep + on-behalf ordering',
          'Up to 5 team members',
          'Priority support (24hr response)'
        ],
        cta: 'ctaProfessional',
        ctaHref: '/wholesaler/register?plan=professional'
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        price: null,
        color: 'amber',
        productLimit: 'unlimited',
        perOrderFee: 0.15,
        contact: true,
        tagline: 'For large operations with multiple warehouses, sales teams, and bespoke needs',
        target: 'Large wholesalers',
        highlights: [
          'Unlimited products',
          '€0.15 per-order fee',
          'Unlimited discounts & flash offers',
          'Unlimited sales reps & team members',
          'Multi-warehouse support',
          'Custom reporting',
          'Dedicated account manager',
          '24/7 priority support (2hr response)'
        ],
        cta: 'ctaEnterprise',
        ctaHref: 'mailto:sales@wholesalehub.gr?subject=Enterprise%20plan%20enquiry'
      }
    ],
    productsLabel: 'products',
    unlimitedLabel: 'Unlimited',
    perOrderShort: 'per order',

    comparison: [
      {
        group: 'Catalog',
        rows: [
          { label: 'Product limit', values: ['1,500', '5,000', 'Unlimited'] },
          { label: 'Bulk CSV upload', values: ['✓', '✓', '✓'] },
          { label: 'Private catalog mode', values: ['—', '✓', '✓'] },
          { label: 'Custom storefront slug', values: ['—', '—', '✓'] },
          { label: 'Per-business catalogs', values: ['—', '—', '✓'] }
        ]
      },
      {
        group: 'Pricing & discounts',
        rows: [
          { label: 'Per-order fee', values: ['€0.40', '€0.25', '€0.15'] },
          { label: 'Customer-specific discounts', values: ['5', 'Unlimited', 'Unlimited'] },
          { label: 'Catalog-wide discounts', values: ['5', '50', 'Unlimited'] },
          { label: 'Product-specific discounts', values: ['10', 'Unlimited', 'Unlimited'] },
          { label: 'Flash offers / month', values: ['—', '5', 'Unlimited'] }
        ]
      },
      {
        group: 'Sales & team',
        rows: [
          { label: 'Team members', values: ['1', '5', 'Unlimited'] },
          { label: 'Sales reps', values: ['—', '1', 'Unlimited'] },
          { label: 'On-behalf ordering', values: ['—', '✓', '✓'] }
        ]
      },
      {
        group: 'Integrations & support',
        rows: [
          { label: 'SoftOne ERP integration', values: ['✓', '✓', '✓'] },
          { label: 'Multi-warehouse', values: ['—', '—', '✓'] },
          { label: 'Dedicated account manager', values: ['—', '—', '✓'] },
          { label: 'Support response time', values: ['48 hours', '24 hours', '2 hours (24/7)'] }
        ]
      }
    ]
  },

  el: {
    heroTitle: 'Απλή τιμολόγηση που μεγαλώνει μαζί σας',
    heroSubtitle: 'Κάθε πακέτο περιλαμβάνει απεριόριστες παραγγελίες, απεριόριστους πελάτες και δίγλωσση υποστήριξη. Επιλέξτε το πακέτο που ταιριάζει στο μέγεθος του καταλόγου σας και στις λειτουργίες που χρειάζεστε.',
    businessCalloutTitle: 'Οι επιχειρήσεις παραγγέλνουν δωρεάν',
    businessCalloutBody: 'Το WholesaleHub είναι πάντα δωρεάν για επιχειρήσεις — χωρίς συνδρομή, χωρίς προμήθειες συναλλαγών, χωρίς κρυφά κόστη. Η παρακάτω τιμολόγηση αφορά μόνο χονδρεμπόρους.',
    popularBadge: 'Πιο δημοφιλές',
    contactBadge: 'Προσαρμοσμένο',
    monthSuffix: '/μήνα',
    contactPrice: 'Επικοινωνήστε μαζί μας',
    perOrderLabel: 'τέλος ανά παραγγελία',
    ctaStarter: 'Ξεκινήστε με Starter',
    ctaProfessional: 'Επιλέξτε Professional',
    ctaEnterprise: 'Επικοινωνία πωλήσεων',
    ctaRegister: 'Δημιουργία λογαριασμού χονδρεμπόρου',
    compareTitle: 'Σύγκριση χαρακτηριστικών',
    compareIntro: 'Όλες οι τιμές δεν περιλαμβάνουν ΦΠΑ. Τα παρακάτω όρια αντικατοπτρίζουν την τρέχουσα διαμόρφωση της πλατφόρμας.',
    faqLinkText: 'Συχνές Ερωτήσεις',
    helpLinkText: 'Κέντρο Βοήθειας',
    contactEmail: 'sales@wholesalehub.gr',

    tiers: [
      {
        id: 'starter',
        name: 'Starter',
        price: 49,
        color: 'blue',
        productLimit: '1.500',
        perOrderFee: 0.40,
        tagline: 'Ό,τι χρειάζεστε για να βγείτε online και να αρχίσετε να λαμβάνετε παραγγελίες',
        target: 'Μικροί χονδρέμποροι',
        highlights: [
          'Έως 1.500 προϊόντα',
          '€0,40 τέλος ανά παραγγελία',
          '5 εκπτώσεις ανά πελάτη',
          '5 εκπτώσεις καταλόγου',
          '10 εκπτώσεις ανά προϊόν',
          '1 μέλος ομάδας',
          'Μαζική μεταφόρτωση CSV',
          'Μεταφορτώσεις τιμολογίων',
          'Υποστήριξη email (απόκριση 48 ωρών)'
        ],
        cta: 'ctaStarter',
        ctaHref: '/wholesaler/register'
      },
      {
        id: 'professional',
        name: 'Professional',
        price: 129,
        color: 'purple',
        productLimit: '5.000',
        perOrderFee: 0.25,
        popular: true,
        tagline: 'Για αναπτυσσόμενους χονδρεμπόρους με τακτικούς πελάτες και ενεργή ροή πωλήσεων',
        target: 'Αναπτυσσόμενοι χονδρέμποροι',
        highlights: [
          'Έως 5.000 προϊόντα',
          '€0,25 τέλος ανά παραγγελία',
          'Απεριόριστες εκπτώσεις πελάτη',
          'Απεριόριστες εκπτώσεις προϊόντος',
          '50 εκπτώσεις καταλόγου',
          '5 flash offers ανά μήνα',
          'Λειτουργία ιδιωτικού καταλόγου',
          '1 εκπρόσωπος πωλήσεων + παραγγελίες εκ μέρους πελάτη',
          'Έως 5 μέλη ομάδας',
          'Προτεραιότητα υποστήριξης (απόκριση 24 ωρών)'
        ],
        cta: 'ctaProfessional',
        ctaHref: '/wholesaler/register?plan=professional'
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        price: null,
        color: 'amber',
        productLimit: 'unlimited',
        perOrderFee: 0.15,
        contact: true,
        tagline: 'Για μεγάλες επιχειρήσεις με πολλαπλές αποθήκες, ομάδες πωλήσεων και εξατομικευμένες ανάγκες',
        target: 'Μεγάλοι χονδρέμποροι',
        highlights: [
          'Απεριόριστα προϊόντα',
          '€0,15 τέλος ανά παραγγελία',
          'Απεριόριστες εκπτώσεις & flash offers',
          'Απεριόριστοι εκπρόσωποι πωλήσεων & μέλη ομάδας',
          'Υποστήριξη πολλαπλών αποθηκών',
          'Προσαρμοσμένη αναφορά',
          'Αφιερωμένος διαχειριστής λογαριασμού',
          '24/7 προτεραιότητα υποστήριξης (απόκριση 2 ωρών)'
        ],
        cta: 'ctaEnterprise',
        ctaHref: 'mailto:sales@wholesalehub.gr?subject=Enterprise%20plan%20enquiry'
      }
    ],
    productsLabel: 'προϊόντα',
    unlimitedLabel: 'Απεριόριστα',
    perOrderShort: 'ανά παραγγελία',

    comparison: [
      {
        group: 'Κατάλογος',
        rows: [
          { label: 'Όριο προϊόντων', values: ['1.500', '5.000', 'Απεριόριστα'] },
          { label: 'Μαζική μεταφόρτωση CSV', values: ['✓', '✓', '✓'] },
          { label: 'Λειτουργία ιδιωτικού καταλόγου', values: ['—', '✓', '✓'] },
          { label: 'Προσαρμοσμένο slug καταστήματος', values: ['—', '—', '✓'] },
          { label: 'Κατάλογοι ανά επιχείρηση', values: ['—', '—', '✓'] }
        ]
      },
      {
        group: 'Τιμολόγηση & Εκπτώσεις',
        rows: [
          { label: 'Τέλος ανά παραγγελία', values: ['€0,40', '€0,25', '€0,15'] },
          { label: 'Εκπτώσεις ανά πελάτη', values: ['5', 'Απεριόριστες', 'Απεριόριστες'] },
          { label: 'Εκπτώσεις καταλόγου', values: ['5', '50', 'Απεριόριστες'] },
          { label: 'Εκπτώσεις ανά προϊόν', values: ['10', 'Απεριόριστες', 'Απεριόριστες'] },
          { label: 'Flash offers / μήνα', values: ['—', '5', 'Απεριόριστα'] }
        ]
      },
      {
        group: 'Πωλήσεις & Ομάδα',
        rows: [
          { label: 'Μέλη ομάδας', values: ['1', '5', 'Απεριόριστα'] },
          { label: 'Εκπρόσωποι πωλήσεων', values: ['—', '1', 'Απεριόριστοι'] },
          { label: 'Παραγγελίες εκ μέρους πελάτη', values: ['—', '✓', '✓'] }
        ]
      },
      {
        group: 'Ενσωματώσεις & Υποστήριξη',
        rows: [
          { label: 'Ενσωμάτωση SoftOne ERP', values: ['✓', '✓', '✓'] },
          { label: 'Πολλαπλές αποθήκες', values: ['—', '—', '✓'] },
          { label: 'Αφιερωμένος διαχειριστής λογαριασμού', values: ['—', '—', '✓'] },
          { label: 'Χρόνος απόκρισης υποστήριξης', values: ['48 ώρες', '24 ώρες', '2 ώρες (24/7)'] }
        ]
      }
    ]
  }
};

export default pricingContent;
